import { useState, useEffect, useRef } from "react";
import { ChevronLeft, Code } from "lucide-react";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChevronRight, ArrowLeft,
  Filter,
} from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { ThemeProvider } from '../../context/ThemeContext';
import PythonRunner from '../../components/PythonRunner';
const CodeBlock = ({ language = "python", children, showLineNumbers = true }) => {
  return (
    <SyntaxHighlighter
      language={language}
      style={oneLight}
      showLineNumbers={showLineNumbers}
      className="rounded-lg"
    >
      {children}
    </SyntaxHighlighter>
  );
};
const toGithub = () => {
  const clientId = 'Ov23lik3NQSmwGhAODnk';
  const redirectUri = `${config.apiBaseUrl}/authapp/github/callback`;
  const scope = 'read:user user:email';
  const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;

  window.open(githubAuthUrl, '_blank', 'width=600,height=700');
};
export default function Algorise() {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [selectedType, setSelectedType] = useState("all");
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [mobileShowFilter, setMobileShowFilter] = useState(false);
  const [sidebarShowFilter, setSidebarShowFilter] = useState(false);
  const mobileFilterRef = useRef(null);
  const sidebarFilterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(event.target)) {
        setMobileShowFilter(false);
      }
    }
    if (mobileShowFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileShowFilter]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarFilterRef.current && !sidebarFilterRef.current.contains(event.target)) {
        setSidebarShowFilter(false);
      }
    }
    if (sidebarShowFilter) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarShowFilter]);

  // 拉取题目数据
  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/get-questions/`);
      const data = await res.json();
      const questionsList = data.questions || [];

      setQuestions(questionsList);
      setFilteredQuestions(questionsList);

      // 默认选中最后一个
      const maxId = Math.max(...questionsList.map((q) => q.id));
      const maxIdIndex = questionsList.findIndex((q) => q.id === maxId);
      setActiveQuestion(maxIdIndex);
    } catch (error) {
      console.error("Fetch questions error:", error);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  // 选项筛选
  const uniqueTypes = ["all", ...new Set(questions.map((q) => q.types?.name || q.types).filter(Boolean))];

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedType(value);
    if (value === "all") {
      setFilteredQuestions(questions);
      setActiveQuestion(0);
    } else {
      const filtered = questions.filter((q) => (typeof q.types === 'object' ? q.types?.name === value : q.types === value));
      setFilteredQuestions(filtered);
      setActiveQuestion(0);
    }

    // 滚动回最左边
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  };
  const [collapsed, setCollapsed] = useState(true);
  // 点击切换题目
  const onQuestionClick = (index) => {
    setActiveQuestion(index);
  };

  if (!filteredQuestions.length) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600">Loading questions...</div>
    );
  }

  return (
    <ThemeProvider>
      <div className="flex flex-col min-h-screen bg-gray-50 w-full overflow-x-hidden">
        {/* 顶部工具栏（移动端显示） */}
        <div className="md:hidden flex flex-col gap-4 px-4 pt-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Return
            </button>

            <div className="relative" ref={mobileFilterRef}>
              <button
                onClick={() => setMobileShowFilter(!mobileShowFilter)}
                className="w-full px-3 py-2 border rounded-xl border-gray-300 shadow-lg bg-white text-left text-sm font-medium text-gray-700 hover:animate-jelly"
              >
                {selectedType === "all" ? "All Types" : selectedType}
              </button>

              {mobileShowFilter && (
                <div className="absolute left-0 top-full z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
                  {uniqueTypes.map((type) => (
                    <div
                      key={type}
                      onClick={() => {
                        handleSelectChange({ target: { value: type } });
                        setMobileShowFilter(false);
                      }}
                      className="px-3 py-2 text-sm hover:bg-gray-100 hover:animate-jelly cursor-pointer"
                    >
                      {type === "all" ? "All Types" : type}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 横向滚动容器：所有题目 */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto no-scrollbar gap-2 px-1 pb-2 w-full"
            style={{
              scrollSnapType: "x mandatory",
            }}
          >
            {filteredQuestions.map((question, index) => (
              <button
                key={question.id}
                onClick={() => onQuestionClick(index)}
                className={`flex-shrink-0 snap-start px-4 py-2 rounded-md font-semibold text-left question-item
                  ${index === activeQuestion ? "bg-darkgray text-white" : "bg-white text-gray-900 hover:bg-gray-200"}
                `}
                title={typeof question.types === 'object' ? question.types?.name : question.types}
              >
                <div className="truncate">{question.title}</div>
                <div className="text-xs italic mt-1">{typeof question.types === 'object' ? question.types?.name : question.types}</div>
              </button>
            ))}
          </div>
        </div>

        <main className="flex w-screen min-h-screen px-4 py-6 gap-6 bg-gray-50">
          {/* 左侧边栏 */}
          <aside
            className={`hidden md:flex transition-all duration-300 ${collapsed ? "w-16" : "w-64"} shrink-0 flex-col gap-4 relative bg-white shadow-lg`}
          >
            {/* 折叠按钮 */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="absolute -right-3 top-10 z-10 bg-gray-200 hover:bg-gray-300 rounded-full p-1 shadow"
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>

            {/* 顶部图标行：返回 + 筛选 */}
            <div className={`flex items-center justify-center transition-all duration-300 ${collapsed ? "gap-1 p-1" : "gap-2 p-2"}`}>
  {/* 返回按钮 */}
  <button
    onClick={() => navigate(-1)}
    className={`
      rounded-full bg-gray-100 hover:bg-gray-200 shadow hover:shadow-md transition-all duration-200
      text-gray-600 hover:text-gray-900
      ${collapsed ? "p-1" : "p-2"}
    `}
    title="Return"
  >
    <ArrowLeft className={`${collapsed ? "w-4 h-4" : "w-5 h-5"}`} />
  </button>

  {/* 筛选按钮 + 弹出下拉框 */}
  <div className="relative" ref={sidebarFilterRef}>
    <button
      onClick={() => setSidebarShowFilter(!sidebarShowFilter)}
      className={`
        rounded-full bg-gray-100 hover:bg-gray-200 shadow hover:shadow-md transition-all duration-200
        text-gray-600 hover:text-gray-900
        ${collapsed ? "p-1" : "p-2"}
      `}
      title="Filter"
    >
      <Filter className={`${collapsed ? "w-4 h-4" : "w-5 h-5"}`} />
    </button>

    {sidebarShowFilter && (
      <div className={`absolute z-20 bg-white border rounded-xl shadow-lg mt-2 w-40 transform transition-all duration-300 ease-out animate-jelly
        ${collapsed ? "left-14 top-8" : "left-6 top-0"}
      `}>
        {uniqueTypes.map((type) => (
          <div
            key={type}
            onClick={() => {
              handleSelectChange({ target: { value: type } });
              setSidebarShowFilter(false);
            }}
            className="px-3 py-2 hover:bg-gray-100 hover:animate-jelly cursor-pointer"
          >
            {type === "all" ? "All Types" : type}
          </div>
        ))}
      </div>
    )}
  </div>
</div>
            {/* 题目列表 */}
            <div
              ref={scrollContainerRef}
              className="flex flex-col gap-2 overflow-y-auto no-scrollbar flex-1 px-2"
            >
              {filteredQuestions.map((question, index) => (
                <button
                  key={question.id}
                  onClick={() => onQuestionClick(index)}
                  title={collapsed ? question.title : undefined}
                  className={`question-item transition-transform duration-300 rounded-md font-semibold text-left
        ${index === activeQuestion ? "bg-darkgray text-white scale-110" : "bg-white text-gray-900 hover:scale-105 hover:bg-gray-200"}
        ${collapsed ? "text-xs px-2 py-2 flex items-center" : "px-4 py-2"}
      `}
                >
                  {collapsed ? (
                    <>
                      <span className="w-6 mr-2 text-center font-bold">{index + 1}</span>
                      <div className="truncate flex-1"></div>
                    </>
                  ) : (
                    <>
                      <div className="truncate">{question.title}</div>
                      <div className="text-xs italic mt-1 truncate">{collapsed ? "" : (typeof question.types === 'object' ? question.types?.name : question.types)}</div>
                    </>
                  )}
                </button>
              ))}
            </div>
          </aside>

          {/* 右侧题目内容区域 */}
          <div className="flex-1 bg-white rounded-lg shadow p-6 overflow-auto">
            <div class="flex items-center justify-between">
            <h2 className="text-2xl font-bold mb-4">{filteredQuestions[activeQuestion].title}</h2>
  <button
  onClick={toGithub}
  aria-label="Login with GitHub"
  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition duration-200"
>
  <svg
    height="24"
    width="24"
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
  >
    <path d="M8 0C3.58 0 0 3.58 0 8a8 8 0 005.47 7.59c.4.07.55-.17.55-.38
      0-.19-.01-.82-.01-1.49-2 .37-2.53-.49-2.69-.94-.09-.23-.48-.94
      -.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21
      1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95
      0-.87.31-1.59.82-2.15-.08-.2-.36-1.01.08-2.11 0 0 .67-.21 2.2.82a7.66
      7.66 0 012-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16
      1.91.08 2.11.51.56.82 1.28.82 2.15 0 3.07-1.87 3.75-3.65
      3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01
      2.2 0 .21.15.46.55.38A8 8 0 0016 8c0-4.42-3.58-8-8-8z" />
  </svg>
</button>
</div>
            <div className="grid grid-cols-1 md:grid-cols-10 gap-6">
              {/* 左侧：描述与示例，占 4 份 */}
              <div className="flex flex-col gap-6 md:col-span-4">
                <section>
                  <h3 className="text-lg font-medium mb-2">Description</h3>
                  <div className="prose max-w-none overflow-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {filteredQuestions[activeQuestion].description}
                    </ReactMarkdown>
                  </div>
                </section>

                <section>
                  {/* <CodeBlock showLineNumbers={false} language="" >
                    {filteredQuestions[activeQuestion].example}
                  </CodeBlock> */}
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {filteredQuestions[activeQuestion].example}
                    </ReactMarkdown>
                </section>
              </div>

              {/* 右侧：代码与解释，占 6 份 */}
              <div className="flex flex-col gap-6 md:col-span-6">
                <section>
                  <PythonRunner initialCode={filteredQuestions[activeQuestion].solution} />
                </section>

                <section>
                  <h3 className="text-lg font-medium mb-2">Explanation</h3>
                  <div className="prose max-w-none">
                    <ReactMarkdown>
                      {filteredQuestions[activeQuestion].explanation}
                    </ReactMarkdown>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </main>

        <style>{`
        /* 移动端隐藏滚动条 */
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* 移动端只显示两个问题 */
        .question-item {
          width: calc(50% - 0.5rem);
          min-width: calc(50% - 0.5rem);
          max-width: 300px;
        }
        .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .question-item {
    transition: transform 0.2s ease;
    transform-origin: left center;
    will-change: transform;
  }

  .question-item:hover {
    transform: scale(1.05);
  }

  .question-item.active {
    transform: scale(1.1);
  }
        @media (min-width: 768px) {
          .question-item {
            width: auto;
            min-width: 150px;
            max-width: 300px;
          }
        }
        @keyframes jelly {
          0% { transform: scale(0.95); }
          40% { transform: scale(1.05); }
          60% { transform: scale(0.98); }
          80% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        .animate-jelly {
          animation: jelly 300ms ease-in-out;
        }
      `}</style>
      </div></ThemeProvider>
  );
}